import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { IGetPaystubPayInfoRes } from '~/types/paystub';
import paystubWeekTimeGuideImg from '../../assets/images/paystub-week-time-guide.svg';
import { ReactComponent as PaystubHelpCircleIcon } from 'assets/icons/paystub-help-circle.svg';
import { ReactComponent as CloseIcon } from 'assets/images/close.svg';

interface IPaystubPaymentTableProps {
  pay2_flag: boolean;
  payment: IGetPaystubPayInfoRes['payment'];
  setPayment: (payment: IGetPaystubPayInfoRes['payment']) => void;
}

const PaystubPaymentTable = ({ pay2_flag, payment, setPayment }: IPaystubPaymentTableProps) => {
  const [weekTimeGuidePopupVisible, setWeekTimeGuidePopupVisible] = useState(false);

  const handleNumberInput = (value: string, callback?: (num: number) => void) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    callback?.(Number(numericValue) || 0);
  };

  const handleTimeInput = (
    value: string,
    isHours: boolean,
    currentHours: number,
    currentMinutes: number,
    callback?: (hours: number, minutes: number) => void,
  ) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    const num = Number(numericValue) || 0;
    if (isHours) {
      callback?.(num, currentMinutes);
    } else {
      callback?.(currentHours, num);
    }
  };

  // 일급인 경우
  if (payment.pay_type === '일급') {
    return (
      <div className="mt-4 flex flex-col overflow-hidden rounded-lg border border-[#e1e1e1]">
        {/* Header */}
        <div className="flex w-full items-center border-b border-[#e1e1e1] bg-[#f2f2f2]">
          <div className="flex h-12 w-[60px] items-center justify-center border-r border-[#e1e1e1]">
            <p className="text-sm font-bold text-[#404040]">구분</p>
          </div>
          <div className="flex h-12 w-[140px] items-center justify-center border-r border-[#e1e1e1]">
            <p className="text-sm font-bold text-[#404040]">항목</p>
          </div>
          <div className="flex h-12 flex-1 items-center justify-center">
            <p className="text-sm font-bold text-[#404040]">입력</p>
          </div>
        </div>

        {/* 일급 */}
        <div className="flex w-full bg-white">
          <div className="flex w-[60px] items-center justify-center border-r border-[#e1e1e1]">
            <p className="text-sm font-bold text-[#404040]">지급</p>
          </div>
          <div className="flex flex-col border-r border-[#e1e1e1]">
            <div className="flex h-[45px] w-[140px] items-center justify-center border-b border-[#e1e1e1]">
              <p className="text-sm font-bold text-[#404040]">
                일급<span className="text-red-500">*</span>
              </p>
            </div>
            <div className="flex h-[45px] w-[140px] items-center justify-center">
              <p className="text-sm font-bold text-[#404040]">
                근무일<span className="text-red-500">*</span>
              </p>
            </div>
          </div>
          <div className="flex flex-1 flex-col">
            {/* 일급 */}
            <div className="flex h-[45px] w-full items-center border-b border-[#e1e1e1] px-4">
              <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                <input
                  type="text"
                  value={payment.salary_pay}
                  onChange={(e) =>
                    handleNumberInput(e.target.value, (value) => setPayment({ ...payment, salary_pay: value }))
                  }
                  className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                  placeholder="0"
                />
              </div>
            </div>
            {/* 근무일 */}
            <div className="flex h-[45px] w-full items-center px-4">
              <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                <input
                  type="text"
                  value={payment.work_day}
                  onChange={(e) =>
                    handleNumberInput(e.target.value, (value) => setPayment({ ...payment, work_day: value }))
                  }
                  className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 월급인 경우
  if (payment.pay_type === '월급') {
    return (
      <div className="mt-4 flex flex-col overflow-hidden rounded-lg border border-[#e1e1e1]">
        {/* Header */}
        <div className="flex w-full items-center border-b border-[#e1e1e1] bg-[#f2f2f2]">
          <div className="flex h-12 w-[60px] items-center justify-center border-r border-[#e1e1e1]">
            <p className="text-sm font-bold text-[#404040]">구분</p>
          </div>
          <div className="flex h-12 w-[140px] items-center justify-center border-r border-[#e1e1e1]">
            <p className="text-sm font-bold text-[#404040]">항목</p>
          </div>
          <div className="flex h-12 flex-1 items-center justify-center">
            <p className="text-sm font-bold text-[#404040]">입력</p>
          </div>
        </div>

        {/* 월급 */}
        <div className="flex w-full bg-white">
          <div className="flex w-[60px] items-center justify-center border-r border-[#e1e1e1]">
            <p className="text-sm font-bold text-[#404040]">지급</p>
          </div>
          <div className="flex flex-col border-r border-[#e1e1e1]">
            <div className="flex h-[45px] w-[140px] items-center justify-center">
              <p className="text-sm font-bold text-[#404040]">
                월급<span className="text-red-500">*</span>
              </p>
            </div>
          </div>
          <div className="flex flex-1 flex-col">
            <div className="flex h-[45px] w-full items-center px-4">
              <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                <input
                  type="text"
                  value={payment.salary_pay}
                  onChange={(e) =>
                    handleNumberInput(e.target.value, (value) => setPayment({ ...payment, salary_pay: value }))
                  }
                  className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 시급인 경우 (기존 코드)
  return (
    <>
      <div className="mt-4 flex flex-col overflow-hidden rounded-lg border border-[#e1e1e1]">
        {/* Header */}
        <div className="flex w-full items-center border-b border-[#e1e1e1] bg-[#f2f2f2]">
          <div className="flex h-12 w-[60px] items-center justify-center border-r border-[#e1e1e1]">
            <p className="text-sm font-bold text-[#404040]">구분</p>
          </div>
          <div className="flex h-12 w-[140px] items-center justify-center border-r border-[#e1e1e1]">
            <p className="text-sm font-bold text-[#404040]">항목</p>
          </div>
          <div className="flex h-12 flex-1 items-center justify-center">
            <p className="text-sm font-bold text-[#404040]">입력</p>
          </div>
        </div>

        {/* 기본급 1 */}
        <div className="flex w-full bg-white">
          {/* Left Category */}
          <div className="flex w-[60px] items-center justify-center border-r border-[#e1e1e1]">
            <p className="text-sm font-bold text-[#404040]">지급</p>
          </div>

          {/* Items Column */}
          <div className="flex flex-col border-r border-[#e1e1e1]">
            <div className="flex h-[45px] w-[140px] items-center justify-center border-b border-[#e1e1e1]">
              <p className="text-sm font-bold text-[#404040]">
                시급<span className="text-red-500">*</span>
              </p>
            </div>
            <div className="flex h-[45px] w-[140px] items-center justify-center border-b border-[#e1e1e1]">
              <p className="text-sm font-bold text-[#404040]">
                근로시간<span className="text-red-500">*</span>
              </p>
            </div>
            <div className="relative flex h-[45px] w-[140px] items-center justify-center gap-[2px] border-b border-[#e1e1e1]">
              <p className="mb-0 text-sm font-bold text-[#404040]">주휴수당 총시간</p>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setWeekTimeGuidePopupVisible(true);
                }}>
                <PaystubHelpCircleIcon className="h-5 w-5 cursor-pointer" />
              </span>
            </div>
            <div className="flex h-[45px] w-[140px] items-center justify-center border-b border-[#e1e1e1]">
              <p className="text-sm font-bold text-[#404040]">초과수당 총시간</p>
            </div>
            <div className="flex h-[45px] w-[140px] items-center justify-center border-b border-[#e1e1e1]">
              <p className="text-sm font-bold text-[#404040]">야간수당 총시간</p>
            </div>
            <div
              className={`flex h-[45px] w-[140px] items-center justify-center ${
                pay2_flag ? 'border-b border-[#e1e1e1]' : ''
              }`}>
              <p className="text-sm font-bold text-[#404040]">휴일수당 총시간</p>
            </div>

            {/* 기본급 2 */}
            {pay2_flag && (
              <div className="flex h-[45px] w-[140px] items-center justify-center border-b border-[#e1e1e1]">
                <p className="text-sm font-bold text-[#404040]">
                  시급2<span className="text-red-500">*</span>
                </p>
              </div>
            )}
            {pay2_flag && (
              <div className="flex h-[45px] w-[140px] items-center justify-center border-b border-[#e1e1e1]">
                <p className="text-sm font-bold text-[#404040]">
                  근로시간2<span className="text-red-500">*</span>
                </p>
              </div>
            )}
            {pay2_flag && (
              <div className="relative flex h-[45px] w-[140px] items-center justify-center gap-[2px] border-b border-[#e1e1e1]">
                <p className="mb-0 text-sm font-bold text-[#404040]">주휴수당 총시간2</p>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setWeekTimeGuidePopupVisible(true);
                  }}>
                  <PaystubHelpCircleIcon className="h-5 w-5 cursor-pointer" />
                </span>
              </div>
            )}
            {pay2_flag && (
              <div className="flex h-[45px] w-[140px] items-center justify-center border-b border-[#e1e1e1]">
                <p className="text-sm font-bold text-[#404040]">초과수당 총시간2</p>
              </div>
            )}
            {pay2_flag && (
              <div className="flex h-[45px] w-[140px] items-center justify-center border-b border-[#e1e1e1]">
                <p className="text-sm font-bold text-[#404040]">야간수당 총시간2</p>
              </div>
            )}
            {pay2_flag && (
              <div className="flex h-[45px] w-[140px] items-center justify-center">
                <p className="text-sm font-bold text-[#404040]">휴일수당 총시간2</p>
              </div>
            )}
          </div>

          {/* Input Column */}
          <div className="flex flex-1 flex-col">
            {/* 시급 */}
            <div className="flex h-[45px] w-full items-center border-b border-[#e1e1e1] px-4">
              <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                <input
                  type="text"
                  value={payment.salary_pay}
                  onChange={(e) =>
                    handleNumberInput(e.target.value, (value) => setPayment({ ...payment, salary_pay: value }))
                  }
                  className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                  placeholder="0"
                />
              </div>
            </div>

            {/* 기본급 */}
            <div className="flex h-[45px] w-full items-center border-b border-[#e1e1e1] px-4">
              <div className="flex flex-1 items-center gap-2">
                <div className="flex flex-1 items-center gap-2">
                  <span className="text-xs text-[#868686]">시간</span>
                  <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                    <input
                      type="text"
                      value={payment.work_time_hour}
                      onChange={(e) =>
                        handleTimeInput(
                          e.target.value,
                          true,
                          payment.work_time_hour,
                          payment.work_time_minute,
                          (hours, minutes) =>
                            setPayment({ ...payment, work_time_hour: hours, work_time_minute: minutes }),
                        )
                      }
                      className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="flex flex-1 items-center gap-2">
                  <span className="text-xs text-[#868686]">분</span>
                  <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                    <input
                      type="text"
                      value={payment.work_time_minute}
                      onChange={(e) =>
                        handleTimeInput(
                          e.target.value,
                          false,
                          payment.work_time_hour,
                          payment.work_time_minute,
                          (hours, minutes) =>
                            setPayment({ ...payment, work_time_hour: hours, work_time_minute: minutes }),
                        )
                      }
                      className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 주휴수당 */}
            <div className="flex h-[45px] w-full items-center border-b border-[#e1e1e1] px-4">
              <div className="flex flex-1 items-center gap-2">
                <div className="flex flex-1 items-center gap-2">
                  <span className="text-xs text-[#868686]">시간</span>
                  <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                    <input
                      type="text"
                      value={payment.week_time_hour}
                      onChange={(e) =>
                        handleTimeInput(
                          e.target.value,
                          true,
                          payment.week_time_hour,
                          payment.week_time_minute,
                          (hours, minutes) =>
                            setPayment({ ...payment, week_time_hour: hours, week_time_minute: minutes }),
                        )
                      }
                      className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="flex flex-1 items-center gap-2">
                  <span className="text-xs text-[#868686]">분</span>
                  <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                    <input
                      type="text"
                      value={payment.week_time_minute}
                      onChange={(e) =>
                        handleTimeInput(
                          e.target.value,
                          false,
                          payment.week_time_hour,
                          payment.week_time_minute,
                          (hours, minutes) =>
                            setPayment({ ...payment, week_time_hour: hours, week_time_minute: minutes }),
                        )
                      }
                      className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 초과수당 */}
            <div className="flex h-[45px] w-full items-center border-b border-[#e1e1e1] px-4">
              <div className="flex flex-1 items-center gap-2">
                <div className="flex flex-1 items-center gap-2">
                  <span className="text-xs text-[#868686]">시간</span>
                  <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                    <input
                      type="text"
                      value={payment.over_time_hour}
                      onChange={(e) =>
                        handleTimeInput(
                          e.target.value,
                          true,
                          payment.over_time_hour,
                          payment.over_time_minute,
                          (hours, minutes) =>
                            setPayment({ ...payment, over_time_hour: hours, over_time_minute: minutes }),
                        )
                      }
                      className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="flex flex-1 items-center gap-2">
                  <span className="text-xs text-[#868686]">분</span>
                  <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                    <input
                      type="text"
                      value={payment.over_time_minute}
                      onChange={(e) =>
                        handleTimeInput(
                          e.target.value,
                          false,
                          payment.over_time_hour,
                          payment.over_time_minute,
                          (hours, minutes) =>
                            setPayment({ ...payment, over_time_hour: hours, over_time_minute: minutes }),
                        )
                      }
                      className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 야간수당 */}
            <div className="flex h-[45px] w-full items-center border-b border-[#e1e1e1] px-4">
              <div className="flex flex-1 items-center gap-2">
                <div className="flex flex-1 items-center gap-2">
                  <span className="text-xs text-[#868686]">시간</span>
                  <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                    <input
                      type="text"
                      value={payment.night_work_time_hour}
                      onChange={(e) =>
                        handleTimeInput(
                          e.target.value,
                          true,
                          payment.night_work_time_hour,
                          payment.night_work_time_minute,
                          (hours, minutes) =>
                            setPayment({ ...payment, night_work_time_hour: hours, night_work_time_minute: minutes }),
                        )
                      }
                      className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="flex flex-1 items-center gap-2">
                  <span className="text-xs text-[#868686]">분</span>
                  <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                    <input
                      type="text"
                      value={payment.night_work_time_minute}
                      onChange={(e) =>
                        handleTimeInput(
                          e.target.value,
                          false,
                          payment.night_work_time_hour,
                          payment.night_work_time_minute,
                          (hours, minutes) =>
                            setPayment({ ...payment, night_work_time_hour: hours, night_work_time_minute: minutes }),
                        )
                      }
                      className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 휴일수당 */}
            <div className={`flex h-[45px] w-full items-center px-4 ${pay2_flag ? 'border-b border-[#e1e1e1]' : ''}`}>
              <div className="flex flex-1 items-center gap-2">
                <div className="flex flex-1 items-center gap-2">
                  <span className="text-xs text-[#868686]">시간</span>
                  <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                    <input
                      type="text"
                      value={payment.holiday_work_time_hour}
                      onChange={(e) =>
                        handleTimeInput(
                          e.target.value,
                          true,
                          payment.holiday_work_time_hour,
                          payment.holiday_work_time_minute,
                          (hours, minutes) =>
                            setPayment({
                              ...payment,
                              holiday_work_time_hour: hours,
                              holiday_work_time_minute: minutes,
                            }),
                        )
                      }
                      className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="flex flex-1 items-center gap-2">
                  <span className="text-xs text-[#868686]">분</span>
                  <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                    <input
                      type="text"
                      value={payment.holiday_work_time_minute}
                      onChange={(e) =>
                        handleTimeInput(
                          e.target.value,
                          false,
                          payment.holiday_work_time_hour,
                          payment.holiday_work_time_minute,
                          (hours, minutes) =>
                            setPayment({
                              ...payment,
                              holiday_work_time_hour: hours,
                              holiday_work_time_minute: minutes,
                            }),
                        )
                      }
                      className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 시급 2 */}
            {pay2_flag && (
              <div className="flex h-[45px] w-full items-center border-b border-[#e1e1e1] px-4">
                <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                  <input
                    type="text"
                    value={payment.salary_pay2}
                    onChange={(e) =>
                      handleNumberInput(e.target.value, (value) => setPayment({ ...payment, salary_pay2: value }))
                    }
                    className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                    placeholder="0"
                  />
                </div>
              </div>
            )}

            {/* 기본급 2 */}
            {pay2_flag && (
              <div className="flex h-[45px] w-full items-center border-b border-[#e1e1e1] px-4">
                <div className="flex flex-1 items-center gap-2">
                  <div className="flex flex-1 items-center gap-2">
                    <span className="text-xs text-[#868686]">시간</span>
                    <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                      <input
                        type="text"
                        value={payment.work_time2_hour}
                        onChange={(e) =>
                          handleTimeInput(
                            e.target.value,
                            true,
                            payment.work_time2_hour,
                            payment.work_time2_minute,
                            (hours, minutes) =>
                              setPayment({ ...payment, work_time2_hour: hours, work_time2_minute: minutes }),
                          )
                        }
                        className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div className="flex flex-1 items-center gap-2">
                    <span className="text-xs text-[#868686]">분</span>
                    <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                      <input
                        type="text"
                        value={payment.work_time2_minute}
                        onChange={(e) =>
                          handleTimeInput(
                            e.target.value,
                            false,
                            payment.work_time2_hour,
                            payment.work_time2_minute,
                            (hours, minutes) =>
                              setPayment({ ...payment, work_time2_hour: hours, work_time2_minute: minutes }),
                          )
                        }
                        className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 주휴수당 */}
            {pay2_flag && (
              <div className="flex h-[45px] w-full items-center border-b border-[#e1e1e1] px-4">
                <div className="flex flex-1 items-center gap-2">
                  <div className="flex flex-1 items-center gap-2">
                    <span className="text-xs text-[#868686]">시간</span>
                    <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                      <input
                        type="text"
                        value={payment.week_time2_hour}
                        onChange={(e) =>
                          handleTimeInput(
                            e.target.value,
                            true,
                            payment.week_time2_hour,
                            payment.week_time2_minute,
                            (hours, minutes) =>
                              setPayment({ ...payment, week_time2_hour: hours, week_time2_minute: minutes }),
                          )
                        }
                        className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div className="flex flex-1 items-center gap-2">
                    <span className="text-xs text-[#868686]">분</span>
                    <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                      <input
                        type="text"
                        value={payment.week_time2_minute}
                        onChange={(e) =>
                          handleTimeInput(
                            e.target.value,
                            false,
                            payment.week_time2_hour,
                            payment.week_time2_minute,
                            (hours, minutes) =>
                              setPayment({ ...payment, week_time2_hour: hours, week_time2_minute: minutes }),
                          )
                        }
                        className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 초과수당 */}
            {pay2_flag && (
              <div className="flex h-[45px] w-full items-center border-b border-[#e1e1e1] px-4">
                <div className="flex flex-1 items-center gap-2">
                  <div className="flex flex-1 items-center gap-2">
                    <span className="text-xs text-[#868686]">시간</span>
                    <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                      <input
                        type="text"
                        value={payment.over_time2_hour}
                        onChange={(e) =>
                          handleTimeInput(
                            e.target.value,
                            true,
                            payment.over_time2_hour,
                            payment.over_time2_minute,
                            (hours, minutes) =>
                              setPayment({ ...payment, over_time2_hour: hours, over_time2_minute: minutes }),
                          )
                        }
                        className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div className="flex flex-1 items-center gap-2">
                    <span className="text-xs text-[#868686]">분</span>
                    <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                      <input
                        type="text"
                        value={payment.over_time2_minute}
                        onChange={(e) =>
                          handleTimeInput(
                            e.target.value,
                            false,
                            payment.over_time2_hour,
                            payment.over_time2_minute,
                            (hours, minutes) =>
                              setPayment({ ...payment, over_time2_hour: hours, over_time2_minute: minutes }),
                          )
                        }
                        className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 야간수당 */}
            {pay2_flag && (
              <div className="flex h-[45px] w-full items-center border-b border-[#e1e1e1] px-4">
                <div className="flex flex-1 items-center gap-2">
                  <div className="flex flex-1 items-center gap-2">
                    <span className="text-xs text-[#868686]">시간</span>
                    <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                      <input
                        type="text"
                        value={payment.night_work_time2_hour}
                        onChange={(e) =>
                          handleTimeInput(
                            e.target.value,
                            true,
                            payment.night_work_time2_hour,
                            payment.night_work_time2_minute,
                            (hours, minutes) =>
                              setPayment({
                                ...payment,
                                night_work_time2_hour: hours,
                                night_work_time2_minute: minutes,
                              }),
                          )
                        }
                        className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div className="flex flex-1 items-center gap-2">
                    <span className="text-xs text-[#868686]">분</span>
                    <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                      <input
                        type="text"
                        value={payment.night_work_time2_minute}
                        onChange={(e) =>
                          handleTimeInput(
                            e.target.value,
                            false,
                            payment.night_work_time2_hour,
                            payment.night_work_time2_minute,
                            (hours, minutes) =>
                              setPayment({
                                ...payment,
                                night_work_time2_hour: hours,
                                night_work_time2_minute: minutes,
                              }),
                          )
                        }
                        className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 휴일수당 */}
            {pay2_flag && (
              <div className="flex h-[45px] w-full items-center px-4">
                <div className="flex flex-1 items-center gap-2">
                  <div className="flex flex-1 items-center gap-2">
                    <span className="text-xs text-[#868686]">시간</span>
                    <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                      <input
                        type="text"
                        value={payment.holiday_work_time2_hour}
                        onChange={(e) =>
                          handleTimeInput(
                            e.target.value,
                            true,
                            payment.holiday_work_time2_hour,
                            payment.holiday_work_time2_minute,
                            (hours, minutes) =>
                              setPayment({
                                ...payment,
                                holiday_work_time2_hour: hours,
                                holiday_work_time2_minute: minutes,
                              }),
                          )
                        }
                        className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div className="flex flex-1 items-center gap-2">
                    <span className="text-xs text-[#868686]">분</span>
                    <div className="flex h-[32px] flex-1 items-center rounded border border-[#e1e1e1] bg-white px-3">
                      <input
                        type="text"
                        value={payment.holiday_work_time2_minute}
                        onChange={(e) =>
                          handleTimeInput(
                            e.target.value,
                            false,
                            payment.holiday_work_time2_hour,
                            payment.holiday_work_time2_minute,
                            (hours, minutes) =>
                              setPayment({
                                ...payment,
                                holiday_work_time2_hour: hours,
                                holiday_work_time2_minute: minutes,
                              }),
                          )
                        }
                        className="w-full bg-transparent text-right text-sm text-[#404040] outline-none placeholder:text-[#b8b8b8]"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 주휴수당 가이드 전체화면 팝업 */}
      {weekTimeGuidePopupVisible &&
        createPortal(
          <div
            className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/60 p-4"
            onClick={() => setWeekTimeGuidePopupVisible(false)}>
            <div
              className="relative max-h-[90vh] w-max max-w-[90vw] rounded-[10px] bg-white p-5 shadow-xl"
              onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="absolute right-3 top-3 rounded p-1 hover:bg-[#f2f2f2]"
                onClick={() => setWeekTimeGuidePopupVisible(false)}
                aria-label="닫기">
                <CloseIcon className="h-6 w-6 text-[#404040]" />
              </button>
              <img
                src={paystubWeekTimeGuideImg}
                alt="주휴수당 총시간 가이드"
                className="block max-h-[85vh] w-auto rounded border border-[#e1e1e1] bg-white object-contain"
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default PaystubPaymentTable;
